import { formatCalendarDate } from '@/lib/format';

import { DAY_MS } from './windows';

/**
 * Day arithmetic for `TrackSource.history()` — the "how far back" answer every
 * source gives the search screen.
 *
 * Days here are UTC calendar days as `YYYY-MM-DD`, because that is what the
 * sources are organised by: the readsb globes write one directory per UTC day,
 * and FR24 states its data start as a date. A pilot's local date is a
 * different thing and is only compared against these at the edge, in the UI.
 */

/** The UTC calendar day an instant falls on. */
export function utcDay(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/** `day` moved by `days`, which may be negative. */
export function addDays(day: string, days: number): string {
  return utcDay(new Date(Date.parse(`${day}T00:00:00Z`) + days * DAY_MS));
}

/** Whole days from `a` to `b`. */
export function daysBetween(a: string, b: string): number {
  return Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / DAY_MS);
}

/** `7 Apr 2024`, as every other date in the app reads. */
export function formatDay(day: string): string {
  return formatCalendarDate(day);
}

/**
 * The later of a search's start and a source's earliest day, or `undefined`
 * when the whole range is before it.
 *
 * A range that straddles the limit is searched from the limit rather than
 * refused: the logbook's "Find" widens an entry's date by a day either side,
 * and refusing that because the day before is out of reach would refuse a
 * flight the source does hold.
 */
export function clampToReach(
  from: Date,
  to: Date,
  earliestDay: string | undefined,
): Date | undefined {
  if (earliestDay === undefined) {
    return from;
  }
  const earliest = new Date(`${earliestDay}T00:00:00Z`);
  if (to < earliest) {
    return undefined;
  }
  return from < earliest ? earliest : from;
}

/**
 * The earliest day for which `exists` answers `true`, found by binary search
 * between `floor` and `latest`.
 *
 * Assumes history is one contiguous run ending at `latest` — true of a globe
 * that deletes its oldest day files first, which is how retention is done. A
 * gap in the middle would be reported as the start of the run after it, which
 * errs toward stating less history than exists rather than more.
 *
 * @returns `floor` if it already exists (history reaches at least that far),
 *   `undefined` if `latest` does not.
 */
export async function findEarliestDay(
  exists: (day: string) => Promise<boolean>,
  floor: string,
  latest: string,
): Promise<string | undefined> {
  if (!(await exists(latest))) {
    return undefined;
  }
  if (await exists(floor)) {
    return floor;
  }
  // Invariant: `lo` is absent, `hi` is present.
  let lo = 0;
  let hi = daysBetween(floor, latest);
  while (hi - lo > 1) {
    const mid = Math.floor((lo + hi) / 2);
    if (await exists(addDays(floor, mid))) {
      hi = mid;
    } else {
      lo = mid;
    }
  }
  return addDays(floor, hi);
}
